// src/components/PDFExporter.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { FiLoader, FiDownload, FiEye, FiFolder, FiTrash2, FiX } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useOutletContext } from 'react-router-dom';
import pdfExportService from '../services/pdfExportService';
import { lireReponseApi } from '../utils/reponseApiTurpe';
import { estConcluante, motifConclusion } from '../utils/resumeTurpe';

// Fetch une image et la convertit en dataURL base64 (PNG).
async function fetchImageAsDataURL(url) {
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    const blob = await r.blob();
    return await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function fmtSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR'); } catch { return iso; }
}

const PDFExporter = ({ analysisData, className = '', buttonText = 'Exporter PDF', motifBlocage = null }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [reports, setReports] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [viewer, setViewer] = useState(null); // { url, name } — preview PDF in-app (pas de nouvel onglet)

  const outlet = useOutletContext?.() || {};
  const session = outlet.session;
  const pdlId = analysisData?.pdl?.id;
  const analysisYear = analysisData?.year;

  const authHeaders = session?.access_token
    ? { Authorization: `Bearer ${session.access_token}` }
    : {};

  // Charge la liste des rapports persistés pour ce PDL
  const loadHistory = useCallback(async () => {
    if (!pdlId || !session?.access_token) return;
    setLoadingHistory(true);
    try {
      const params = new URLSearchParams({ pdl_id: pdlId });
      if (analysisYear) params.set('analysis_year', String(analysisYear));
      const r = await fetch(`/api/turpe/reports?${params.toString()}`, { headers: authHeaders });
      const j = await r.json();
      if (j.success) setReports(j.data || []);
    } catch (e) {
      console.warn('[PDFExporter] loadHistory:', e?.message);
    } finally {
      setLoadingHistory(false);
    }
  }, [pdlId, analysisYear, session?.access_token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (historyOpen) loadHistory();
  }, [historyOpen, loadHistory]);

  const handleExportPDF = async () => {
    if (!analysisData?.analysisResults?.turpe6_details) {
      toast.error("Données d'analyse incomplètes pour générer le PDF.");
      return;
    }
    setIsExporting(true);
    try {
      // 1. Résumé frais (statut et montants du serveur). Une erreur n'est plus avalée :
      // sans résumé, pas de PDF (règle du 24/10/2026 : PDF client seulement si concluant).
      const r = await fetch(`/api/turpe/get-analysis-summary/${pdlId}`, { headers: authHeaders });
      const analysisSummary = (await lireReponseApi(r, 'Résumé de l\'analyse'))?.data || null;
      if (!estConcluante(analysisSummary)) {
        const m = motifConclusion(analysisSummary?.resume);
        toast.error(m ? `${m.titre} : pas de rapport client. ${m.motifs.join(' ')}` : "Aucune analyse enregistrée : pas de rapport.");
        return;
      }

      // 2. Logo
      const logoDataUrl = await fetchImageAsDataURL('/logo_energygroup.png');
      const payload = { ...analysisData, analysisSummary, logoDataUrl };
      const fileName = `Rapport_TURPE_${analysisData.pdl?.site_name?.replace(/\s+/g, '_') || 'export'}.pdf`;

      // 3. Télécharger localement (comportement historique préservé)
      pdfExportService.downloadPDF(payload, fileName);

      // 4. Enregistrer dans Soft (Supabase Storage + table turpe_reports)
      if (pdlId && session?.access_token) {
        try {
          const content_base64 = pdfExportService.getPDFBase64(payload);
          const saveResp = await fetch('/api/turpe/reports', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders },
            body: JSON.stringify({
              pdl_id: pdlId,
              analysis_year: analysisYear,
              file_name: fileName,
              content_base64,
              metadata: {
                chosen_optimization: analysisSummary?.chosen_optimization || null,
                chosen_version_of_use: analysisSummary?.chosen_version_of_use || null,
                best_saving_eur: analysisSummary?.best_saving_eur || null,
                manual_saving_eur: analysisSummary?.manual_saving_eur || null,
                // Montants du résumé PARTAGÉ (version : coût actuelle − coût retenue).
                economie_ps_eur: analysisSummary?.resume?.ps?.economie_eur ?? null,
                economie_version_eur: analysisSummary?.resume?.fta?.economie_eur ?? null,
                economie_totale_eur: analysisSummary?.resume?.economie_totale_eur ?? null,
              },
            }),
          });
          await lireReponseApi(saveResp, 'Enregistrement du rapport');
          toast.success('PDF généré et enregistré dans Soft.');
        } catch (e) {
          console.error('[PDFExporter] save error:', e?.message);
          toast.error(`PDF téléchargé mais NON enregistré : ${e.message}`);
        }
      } else {
        toast.success('PDF généré.');
      }
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      toast.error('Une erreur est survenue lors de la génération du PDF.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleView = async (report) => {
    try {
      const r = await fetch(`/api/turpe/reports/${report.id}/url`, { headers: authHeaders });
      const j = await r.json();
      // Preview in-app (modale flottante) — pas d'ouverture de nouvel onglet.
      if (j.success && j.data?.url) setViewer({ url: j.data.url, name: report.file_name });
      else toast.error('Impossible d\'ouvrir le rapport.');
    } catch (e) {
      toast.error('Erreur d\'ouverture du rapport.');
    }
  };

  const handleDelete = async (reportId) => {
    try {
      const r = await fetch(`/api/turpe/reports/${reportId}`, { method: 'DELETE', headers: authHeaders });
      if (r.ok) {
        toast.success('Rapport supprimé.');
        setReports(prev => prev.filter(x => x.id !== reportId));
      } else {
        toast.error('Suppression refusée (admin requis).');
      }
    } catch {
      toast.error('Erreur lors de la suppression.');
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <button
          onClick={handleExportPDF}
          disabled={isExporting || !analysisData || !!motifBlocage}
          title={motifBlocage ? `Pas de rapport client — ${motifBlocage}` : undefined}
          data-testid="turpe-pdf"
          className={`px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors ${className}`}
        >
          {isExporting ? (
            <><FiLoader className="h-4 w-4 animate-spin" /><span>Génération...</span></>
          ) : (
            <><FiDownload className="h-4 w-4" /><span>{buttonText}</span></>
          )}
        </button>
        <button
          type="button"
          onClick={() => setHistoryOpen(true)}
          disabled={!pdlId}
          title="Voir les rapports enregistrés"
          className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1 transition-colors"
        >
          <FiFolder className="h-4 w-4" />
          <span className="text-xs">Historique</span>
        </button>
      </div>

      {historyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setHistoryOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-[680px] max-w-[95vw] p-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <FiFolder className="h-4 w-4 text-emerald-600" />
                Rapports TURPE enregistrés
              </h3>
              <button onClick={() => setHistoryOpen(false)} className="text-gray-400 hover:text-gray-600">
                <FiX className="h-4 w-4" />
              </button>
            </div>

            {loadingHistory ? (
              <div className="py-6 text-center text-xs text-gray-500">
                <FiLoader className="h-4 w-4 animate-spin mx-auto mb-1" />
                Chargement…
              </div>
            ) : reports.length === 0 ? (
              <div className="py-6 text-center text-xs text-gray-500">
                Aucun rapport enregistré pour ce PDL.
              </div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto">
                <table className="min-w-full text-[11px]">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left font-semibold text-gray-500">Nom</th>
                      <th className="px-2 py-1 text-left font-semibold text-gray-500">Exercice</th>
                      <th className="px-2 py-1 text-left font-semibold text-gray-500">Généré le</th>
                      <th className="px-2 py-1 text-right font-semibold text-gray-500">Taille</th>
                      <th className="px-2 py-1 text-right font-semibold text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reports.map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-2 py-1.5 font-medium text-gray-800 truncate max-w-[260px]" title={r.file_name}>{r.file_name}</td>
                        <td className="px-2 py-1.5 text-gray-600">{r.analysis_year}</td>
                        <td className="px-2 py-1.5 text-gray-600">{fmtDate(r.generated_at)}</td>
                        <td className="px-2 py-1.5 text-right text-gray-600">{fmtSize(r.file_size_bytes)}</td>
                        <td className="px-2 py-1.5 text-right">
                          <button
                            onClick={() => handleView(r)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] text-emerald-700 hover:bg-emerald-50"
                            title="Visualiser le rapport"
                          >
                            <FiEye className="h-3 w-3" /> Voir
                          </button>
                          <button
                            onClick={() => handleDelete(r.id)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] text-red-600 hover:bg-red-50 ml-1"
                            title="Supprimer (admin uniquement)"
                          >
                            <FiTrash2 className="h-3 w-3" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview PDF in-app (modale flottante) — remplace l'ouverture en nouvel onglet. */}
      {viewer && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={() => setViewer(null)}>
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-[900px] max-w-[95vw] h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2 min-w-0">
                <FiEye className="h-4 w-4 text-emerald-600 shrink-0" />
                <span className="truncate" title={viewer.name}>{viewer.name}</span>
              </h3>
              <div className="flex items-center gap-3 shrink-0">
                <a href={viewer.url} download={viewer.name} className="text-gray-400 hover:text-gray-600" title="Télécharger">
                  <FiDownload className="h-4 w-4" />
                </a>
                <button onClick={() => setViewer(null)} className="text-gray-400 hover:text-gray-600" title="Fermer">
                  <FiX className="h-4 w-4" />
                </button>
              </div>
            </div>
            <iframe src={viewer.url} title={viewer.name} className="flex-1 w-full rounded-b-lg" />
          </div>
        </div>
      )}
    </>
  );
};

export default PDFExporter;
